import React, { useMemo, useState, useEffect, useCallback, memo } from 'react';
import FileAttachment from './FileAttachment';
import { getFileUrl } from '../../../api/files';
import { MoonLoader } from 'react-spinners';
import ImageViewer from '../../common/ImageViewer';
import VideoPlayer from '../../common/VideoPlayer';

import styles from './styles/MessageAttachments.module.css';

const MessageAttachments = ({ attachments, isAssistantMessage = false }) => {
    const [loadingImages, setLoadingImages] = useState({});
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [resolvedTypes, setResolvedTypes] = useState({});

    useEffect(() => {
        const initialLoadingState = {};
        attachments?.forEach(file => {
            if (file.type.startsWith('image/')) {
                initialLoadingState[file.id] = true;
            }
        });
        setLoadingImages(initialLoadingState);
    }, [attachments]);

    // Resolve actual content-type by calling the file endpoint and reading headers
    useEffect(() => {
        let cancelled = false;
        const candidates = (attachments || []).filter(f => {
            if (!f || !f.id || !f.url) return false;
            const isApiFile = /\/api\/files\//.test(String(f.url));
            if (!isApiFile) return false;
            if (resolvedTypes[f.id]) return false; // already resolved
            // We mainly want to upgrade derived images to videos when needed
            return !f.type || f.type.startsWith('image/');
        });

        const resolveType = async file => {
            const url = getFileUrl(file.url);
            try {
                const res = await fetch(url, { method: 'HEAD', credentials: 'include' });
                if (res.ok) {
                    const ct = res.headers.get('content-type');
                    if (ct && !cancelled) {
                        setResolvedTypes(prev => (prev[file.id] ? prev : { ...prev, [file.id]: ct }));
                    }
                }
            } catch (_e) {
                // Ignore; we keep original type
            }
        };

        candidates.forEach(resolveType);
        return () => { cancelled = true; };
    }, [attachments, resolvedTypes]);

    const getEffectiveType = useCallback(file => {
        return resolvedTypes[file.id] || file.type || '';
    }, [resolvedTypes]);

    // Divide files into images, videos and other files based on effective type
    const images = useMemo(() => 
        attachments?.filter(file => getEffectiveType(file).startsWith('image/')) || [], 
        [attachments, getEffectiveType]
    );

    const videos = useMemo(() => {
        const videos = attachments?.filter(file => getEffectiveType(file).startsWith('video/')) || [];
        const newVideos = videos.map(file => ({ ...file, type: getEffectiveType(file) }));
        return newVideos;
    }, [attachments, getEffectiveType]);

    const files = useMemo(() => 
        attachments?.filter(file => {
            const t = getEffectiveType(file);
            return !t.startsWith('image/') && !t.startsWith('video/');
        }) || [], 
        [attachments, getEffectiveType]
    );

    const handleImageLoad = imageId => {
        setLoadingImages(prev => ({ ...prev, [imageId]: false }));
    };

    const handleImageClick = useCallback(index => {
        setSelectedImageIndex(index);
    }, []);

    if (!attachments || attachments.length === 0) return null;

    return (
        <div className={styles.attachmentsContainer}>
            {/* Display images */}
            {images.length > 0 && (
                <div className={`${styles.imagesGrid} ${isAssistantMessage ? styles.assistantImagesGrid : ''}`}>
                    {images.map((file, idx) => (
                        <div key={file.id} className={styles.imageWrapper}>
                            <div className={styles.imageContainer}>
                                {loadingImages[file.id] && (
                                    <div className={styles.imageLoader}>
                                        <MoonLoader color="#000000" size={24} />
                                    </div>
                                )}
                                <img 
                                    src={getFileUrl(file.url)} 
                                    alt={file.name}
                                    className={`${styles.image} ${loadingImages[file.id] ? styles.loading : ''}`}
                                    onLoad={() => handleImageLoad(file.id)}
                                    onClick={() => handleImageClick(idx)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Display videos */}
            {videos.length > 0 && (
                <div className={`${styles.videosContainer} ${isAssistantMessage ? styles.assistantVideosContainer : ''}`}>
                    {videos.map(file => (
                        <div key={file.id} className={styles.videoWrapper}>
                            <VideoPlayer
                                src={getFileUrl(file.url)}
                                type={file.type || 'video/mp4'}
                                autoPlay={true}
                                muted={true}
                                loop={true}
                                showSoundButton={true}
                                showControls={true}
                            />
                        </div>
                    ))}
                </div>
            )}

            {selectedImageIndex !== null && (
                <ImageViewer
                    items={images.map(f => ({ src: getFileUrl(f.url), alt: f.name }))}
                    startIndex={selectedImageIndex}
                    onClose={() => setSelectedImageIndex(null)}
                />
            )}

            {/* Display other files */}
            {files.length > 0 && (
                <div className={styles.filesContainer}>
                    {files.map(file => (
                        <FileAttachment
                            key={file.id}
                            file={file}
                            id={file.id}
                            readOnly
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default memo(MessageAttachments);
